import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../../../Context/AppContext';
import ShareOptionsModalV2 from '../../../../Share/ShareOptionsModalV2/ShareOptionsModalV2';
import getUserDate from '../../../../../util/getUserDate';
import requestAxios from '../../../../../util/requestAxios';
import { readableFColor } from '../../../../../util/readableBrandColor';
import OolaWheelMilestoneV2 from '../../../../PortalV2/Steps/OolaWheelMilestoneV2';
import StreakCelebrateV2 from '../../../../PortalV2/Steps/StreakCelebrateV2';
import './FinalStep1V2.css';

function areaScore(f) {
    if (!f) return null;
    let total = null;
    if (f.TotalScore != null && !Number.isNaN(Number(f.TotalScore))) {
        total = Number(f.TotalScore);
    } else {
        const q = (f.questions || []).reduce(
            (pv, cv) => pv + (cv.score != null ? Number(cv.score) : 0),
            0
        );
        total = q > 0 ? q : null;
    }
    if (total == null) return null;
    return {
        name: f.name || 'Area',
        score: total / 10,
        color: readableFColor(f.name, f.color || '#15c285'),
    };
}

/**
 * Portal V2 — Step 1 Oola Score reveal. Classic FinalStep1 stays on /steps.
 */
export default function FinalStep1V2({ state, endStep1, myr }) {
    const [activeShare, setActiveShare] = useState(false);
    const [shareData, setShareData] = useState({});
    const [loading, setLoading] = useState(false);
    const [ctaReady, setCtaReady] = useState(false);
    const { checkUserSession } = useContext(AppContext);
    const currentUser = checkUserSession() || {};
    const firstName = currentUser.firstName || 'Oola';
    const isMyr = myr === 1;

    useEffect(() => {
        const t = window.setTimeout(() => setCtaReady(true), 900);
        return () => window.clearTimeout(t);
    }, []);

    const extremes = useMemo(() => {
        const scored = (state?.Fs || [])
            .map(areaScore)
            .filter((x) => x && x.score != null && !Number.isNaN(x.score));
        if (!scored.length) return { high: null, low: null };
        let high = scored[0];
        let low = scored[0];
        scored.forEach((s) => {
            if (s.score > high.score) high = s;
            if (s.score < low.score) low = s;
        });
        return { high, low };
    }, [state?.Fs]);

    const handleShare = async () => {
        setLoading(true);
        const { error, data } = await requestAxios({
            url: `Customer/GetShareContent/1`,
            token: currentUser.token,
        });

        if (error) {
            window.alert(
                'Sorry there was an error on sharing, please try again'
            );
            setLoading(false);
            return;
        }
        setShareData(data);
        setActiveShare(true);
        setLoading(false);
    };

    const primaryLabel = isMyr ? 'Go to dashboard' : 'Start Step 2';

    return (
        <div className="pv2-score-reveal">
            <div className="pv2-score-reveal__panel">
                <div className="pv2-score-reveal__glow" aria-hidden="true" />

                <div className="pv2-score-reveal__grid">
                    <div className="pv2-score-reveal__copy">
                        <p className="pv2-score-reveal__eyebrow">
                            {isMyr
                                ? 'Mid-year review complete'
                                : 'Step 1 complete'}
                        </p>

                        <h2 className="pv2-score-reveal__title">
                            {isMyr
                                ? 'Your updated Oola Score is ready'
                                : 'Your Oola Score is ready'}
                        </h2>

                        <p className="pv2-score-reveal__hello">
                            Great work, {firstName}
                        </p>

                        <p className="pv2-score-reveal__body">
                            {isMyr
                                ? 'You completed your mid-year review across all 7 F’s of Oola. Your OolaWheel and OolaScore are ready to compare against your starting point.'
                                : 'You completed all 7 F’s of Oola. Your OolaWheel and OolaScore are now ready. This is your starting point for building your life blueprint.'}
                        </p>

                        <p className="pv2-score-reveal__emotion">
                            This is your starting point — not your final
                            destination.
                        </p>
                    </div>

                    <div className="pv2-score-reveal__visual">
                        <div
                            className="pv2-score-reveal__wheel-aura"
                            aria-hidden="true"
                        />
                        <div className="pv2-score-reveal__wheel">
                            <StreakCelebrateV2 active />
                            <OolaWheelMilestoneV2
                                Fs={state.Fs || []}
                                activeIndex={6}
                                celebrate
                            />
                        </div>

                        <div
                            className="pv2-score-reveal__chips"
                            aria-label="Score insights"
                        >
                            {extremes.high ? (
                                <div className="pv2-score-reveal__chip">
                                    <span className="pv2-score-reveal__chip-k">
                                        Highest
                                    </span>
                                    <strong
                                        style={{ color: extremes.high.color }}
                                    >
                                        {extremes.high.name}{' '}
                                        {extremes.high.score.toFixed(1)}
                                    </strong>
                                </div>
                            ) : null}
                            {extremes.low ? (
                                <div className="pv2-score-reveal__chip">
                                    <span className="pv2-score-reveal__chip-k">
                                        Lowest
                                    </span>
                                    <strong
                                        style={{ color: extremes.low.color }}
                                    >
                                        {extremes.low.name}{' '}
                                        {extremes.low.score.toFixed(1)}
                                    </strong>
                                </div>
                            ) : null}
                            <div className="pv2-score-reveal__chip">
                                <span className="pv2-score-reveal__chip-k">
                                    Next step
                                </span>
                                <strong>
                                    {isMyr ? 'Dashboard' : 'Goals'}
                                </strong>
                            </div>
                        </div>
                    </div>

                    <div
                        className="pv2-score-reveal__pills"
                        aria-label="Progress summary"
                    >
                        <div className="pv2-score-reveal__pill">
                            <span className="pv2-score-reveal__pill-dot pv2-score-reveal__pill-dot--green" />
                            <span className="pv2-score-reveal__pill-label">
                                7 areas assessed
                            </span>
                            <span className="pv2-score-reveal__pill-extra">
                                · Oola Score ready
                            </span>
                        </div>
                        <div className="pv2-score-reveal__pill">
                            <span className="pv2-score-reveal__pill-dot pv2-score-reveal__pill-dot--gold" />
                            <span>Oola Score calculated</span>
                        </div>
                        <div className="pv2-score-reveal__pill">
                            <span className="pv2-score-reveal__pill-dot pv2-score-reveal__pill-dot--next" />
                            <span>
                                {isMyr ? 'Next: Dashboard' : 'Next: Goals'}
                            </span>
                        </div>
                    </div>

                    <div
                        className={`pv2-score-reveal__actions${
                            ctaReady ? ' is-ready' : ''
                        }`}
                    >
                        <button
                            type="button"
                            className="pv2-score-reveal__cta"
                            onClick={() => endStep1(state)}
                        >
                            {primaryLabel}
                        </button>
                        {!isMyr ? (
                            <button
                                type="button"
                                className="pv2-score-reveal__share"
                                onClick={handleShare}
                            >
                                {loading ? 'Loading…' : 'Share'}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>

            {activeShare ? (
                <ShareOptionsModalV2
                    webAlias={shareData.webAlias}
                    setActiveShare={setActiveShare}
                    imageUrl={shareData.socialMediaImageUrl}
                    text={`${String(shareData.socialMediaTitle || '').replace(
                        'date',
                        getUserDate()
                    )} ${shareData.socialMediaMessage || ''}`.trim()}
                />
            ) : null}
        </div>
    );
}
